import { useCallback } from 'react';
import { type LatLng } from '~/_shared/types/latLng';
import {
  jtsParse, operations,
} from '~/_shared/types/polygon/jsts.utils';
import {
  convertLatLngToCoordinate, convertMultiPolygonToGeometry,
} from '~/_shared/types/polygon/polygon.utils';
import {
  type GroupRadiusProximity, isDriveTimePolygon, isGroupRadius, isIndividualRadius, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import { type SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { notNull } from '~/_shared/utils/typeGuards';
import {
  areRadialDistancesEqual,
  findRowIdsToRenderForColumnIdInGroup, getGroupRadiusProximityDetails, isRadiusAtLocation,
} from '~/proximity/proximity.helpers';
import type { OverlappingProximity } from '~/store/frontendState/activeMapElements/activeMapElements.state';
import { useHiddenProximityIdsSelector } from '~/store/mapSettings/toolsState/visibility/visibility.selectors';
import { checkIfSpreadsheetRowIdMeetsFilteringCriteria } from '~/store/spreadsheetData/spreadsheetData.helpers';
import {
  useFilteredSpreadsheetRowIds, useLatLngSpreadsheetData, useSpreadSheetData,
} from '../useSpreadsheetData.hook';

type GroupRadiusProximityLocation = Readonly<{ spreadsheetRowId: SpreadsheetRowId; latLng: LatLng | null }>;
export type GroupRadiusProximityLocations = ReadonlyArray<GroupRadiusProximityLocation>;

export const useProximityLocation = () => {
  const spreadsheetData = useSpreadSheetData().spreadsheetData;
  const latLngData = useLatLngSpreadsheetData();
  const { filteredRowIds } = useFilteredSpreadsheetRowIds();
  const hiddenProximityIds = useHiddenProximityIdsSelector();

  const getGroupRadiusProximityLocations = useCallback((proximity: GroupRadiusProximity, ignoreFilters: boolean = false): GroupRadiusProximityLocations => {
    const { columnId, group: groupId } = proximity.data.data;
    const rowIds = findRowIdsToRenderForColumnIdInGroup(spreadsheetData, columnId, groupId);

    return rowIds.map((rowId) => {
      const spreadsheetRowId = {
        spreadsheetId: columnId.spreadsheetId,
        rowId,
      };

      if (!ignoreFilters && !checkIfSpreadsheetRowIdMeetsFilteringCriteria(filteredRowIds, spreadsheetRowId)) {
        return { spreadsheetRowId, latLng: null };
      }

      const latLng = latLngData.getRow(spreadsheetRowId);

      return {
        spreadsheetRowId,
        latLng: latLng ?? null,
      };
    }).filter(notNull);

  }, [filteredRowIds, latLngData, spreadsheetData]);

  const getProximityIdsAtLocation = useCallback(
    (point: LatLng, proximities: ReadonlyArray<Proximity>): OverlappingProximity[] => {
      // Pre-parse the clicked point for DTP checks
      const clickPoint = jtsParse({
        type: 'Point',
        coordinates: convertLatLngToCoordinate(point),
      });

      return proximities
        .filter(p => !hiddenProximityIds.includes(p.id))
        .flatMap(proximity => {
          if (isIndividualRadius(proximity)) {
            return isRadiusAtLocation({ point, center: proximity.data.data, proximity })
              ? [{ proximityId: proximity.id }]
              : [];
          }

          if (isGroupRadius(proximity)) {
            return getGroupRadiusProximityLocations(proximity)
              .filter(loc => {
                const individualRadiusSettings = getGroupRadiusProximityDetails(proximity, loc.spreadsheetRowId);
                const overridenProximity = areRadialDistancesEqual(proximity.data, individualRadiusSettings)
                  ? proximity
                  : {
                    ...proximity,
                    data: {
                      ...proximity.data,
                      ...individualRadiusSettings,
                    },
                  };

                return loc.latLng && isRadiusAtLocation({ point, center: loc.latLng, proximity: overridenProximity });
              })
              .map(loc => ({
                proximityId: proximity.id,
                spreadsheetRowId: loc.spreadsheetRowId,
              }));
          }

          if (isDriveTimePolygon(proximity)) {
            const paths = proximity.data.paths;
            return paths && operations.relate.intersects(
              clickPoint,
              jtsParse(convertMultiPolygonToGeometry(paths))
            )
              ? [{ proximityId: proximity.id }]
              : [];
          }

          return [];
        });
    }, [hiddenProximityIds, getGroupRadiusProximityLocations],
  );

  return {
    getGroupRadiusProximityLocations,
    getProximitiesAtLocation: getProximityIdsAtLocation,
  };
};
